import React, { useState, useEffect } from 'react';
import { X, Truck, User, Hash, FileText, Camera, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormField, Textarea } from '@/components/ui/FormComponents';

export const ReceiveMaterialModal = ({ isOpen, onClose, request, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [statusNotes, setStatusNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [receivedQtys, setReceivedQtys] = useState({});
  const [confirmFullDelivery, setConfirmFullDelivery] = useState(false);

  useEffect(() => {
    if (confirmFullDelivery && request?.items) {
      const fullQtys = {};
      request.items.forEach((item, idx) => {
        const issued = item.issuedQuantity || 0;
        const delivered = item.deliveredQuantity || 0;
        fullQtys[idx] = Math.max(0, issued - delivered);
      });
      setReceivedQtys(fullQtys);
    }
  }, [confirmFullDelivery, request]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files]);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleQtyChange = (idx, val, max) => {
    const num = Math.min(max, Math.max(0, Number(val)));
    setReceivedQtys(prev => ({ ...prev, [idx]: num }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      await onConfirm({ statusNotes, photos, receivedItemsQtys: receivedQtys });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = confirmFullDelivery || Object.keys(receivedQtys).some(k => receivedQtys[k] > 0);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Material Site Receival Confirmation" 
      size="max-w-3xl"
      footer={
        <div className="flex flex-col gap-4 w-full">
            {/* Delivery Confirmation Card */}
            <div className="flex items-center gap-4 p-4 bg-blue-50/40 border border-[#2b448f]/10 rounded-xl group cursor-pointer hover:bg-blue-50/60 transition-all duration-300"
                    onClick={() => setConfirmFullDelivery(!confirmFullDelivery)}>
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center border-2 transition-all duration-300 ${
                    confirmFullDelivery ? 'bg-[#2b448f] border-[#2b448f] shadow-md shadow-blue-100' : 'bg-white border-slate-200 group-hover:border-[#2b448f]/50'
                }`}>
                    {confirmFullDelivery && <X className="w-4 h-4 text-white" strokeWidth={4} />}
                </div>
                <div className="flex flex-col">
                    <span className="text-[12px] font-bold text-[#2b448f] uppercase tracking-wide">Confirm Full Delivery</span>
                    <span className="text-[10px] text-slate-400 font-medium tracking-tight mt-0.5">Check this if all dispatched materials match your physical receipt</span>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row items-center gap-3 w-full">
                <Button 
                    type="button"
                    variant="secondary" 
                    className="w-full sm:flex-1 h-11 rounded-xl text-[12px] font-bold uppercase tracking-widest border-slate-200 text-slate-500 hover:bg-slate-50"
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button 
                    variant='orange'
                    loading={loading}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                    className="w-full sm:flex-1 h-11 rounded-xl text-[12px] font-bold uppercase tracking-widest shadow-lg shadow-blue-200/50 disabled:opacity-50"
                >
                    {confirmFullDelivery ? "✅ Confirm" : "✅ Receive Quantities"}
                </Button>
            </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6 font-poppins py-1">
        {/* Verification Table */}
        <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
                <div className="w-1 h-3 rounded-full bg-[#2b448f]" />
                <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">Item Verification</span>
            </div>
            <div className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-100">
                      <th className="px-5 py-3.5 text-[13px] font-bold text-slate-400 uppercase tracking-widest w-28 text-center">Receive</th>
                      <th className="px-5 py-3.5 text-[13px] font-bold text-slate-400 uppercase tracking-widest">Material Detail</th>
                      <th className="px-5 py-3.5 text-[13px] font-bold text-slate-400 uppercase tracking-widest text-center">Expected</th>
                      <th className="px-5 py-3.5 text-[13px] font-bold text-slate-400 uppercase tracking-widest text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {request?.items?.map((item, idx) => {
                      const issued = item.issuedQuantity || 0;
                      const delivered = item.deliveredQuantity || 0;
                      const pending = Math.max(0, issued - delivered);
                      const isFullyDelivered = delivered >= (item.quantity || 0);
                      
                      return (
                        <tr key={idx} className={`hover:bg-slate-50/30 transition-colors duration-200 ${isFullyDelivered ? 'opacity-40' : ''}`}>
                          <td className="px-4 py-4 text-center w-28">
                            {!isFullyDelivered ? (
                               <input 
                                  type="number"
                                  min="0"
                                  disabled={confirmFullDelivery || pending === 0}
                                  max={pending}
                                  placeholder={confirmFullDelivery ? "MAX" : "0"}
                                  value={confirmFullDelivery ? pending : (receivedQtys[idx] ?? '')}
                                  onChange={(e) => handleQtyChange(idx, e.target.value, pending)}
                                  className={`w-full text-center h-9 text-[13px] font-bold border rounded-lg focus:outline-none transition-all tabular-nums ${
                                    confirmFullDelivery 
                                        ? 'bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed' 
                                        : 'bg-white text-slate-700 border-slate-200 focus:border-[#2b448f] focus:ring-2 focus:ring-blue-100'
                                  }`}
                               />
                            ) : (
                               <div className="flex items-center justify-center h-9">
                                  <CheckCircle2 className="w-5 h-5 text-emerald-500" strokeWidth={3} />
                               </div>
                            )}
                          </td>
                          <td className="px-5 py-4">
                             <div className="flex flex-col gap-0.5">
                                <span className="text-[12px] font-bold text-slate-700 tracking-tight">{item.itemName}</span>
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Inventory Stock</span>
                             </div>
                          </td>
                          <td className="px-5 py-4 text-center">
                             <div className="flex flex-col items-center">
                                <span className="text-[12px] font-bold text-[#2b448f] tabular-nums">{pending}</span>
                                {delivered > 0 && <span className="text-[9px] font-bold text-amber-500 whitespace-nowrap">Prev: {delivered}</span>}
                             </div>
                          </td>
                          <td className="px-5 py-4 text-right">
                             <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-md tracking-widest border ${
                                isFullyDelivered 
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                                    : 'bg-blue-50 text-blue-600 border-blue-100'
                             }`}>
                                {isFullyDelivered ? 'Received' : 'Pending'}
                             </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>
        </div>

        {/* Form Fields Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
           {/* Left: Notes & Alert */}
           <div className="flex flex-col gap-4">
              <FormField label="Acknowledgement Remarks">
                <Textarea 
                  placeholder="Optional: Notes about material condition, shortage, etc..."
                  rows={4}
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  className="bg-white rounded-xl text-[12px] font-medium border-slate-200 focus:border-[#2b448f] focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-slate-300"
                />
              </FormField>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50/50 border border-amber-200/30">
                <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-[11px] font-semibold text-amber-800/80 leading-relaxed italic">
                  Critical: Physical verification is mandatory. By confirming, you acknowledge the receipt of mentioned quantities on site.
                </p>
              </div>
           </div>

           {/* Right: Photos */}
           <div className="flex flex-col gap-3">
              <span className="text-[12px] font-bold text-slate-700 px-1">Site Verification Photos</span>
              <div className="flex flex-col gap-4">
                  <label 
                    className="h-28 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 text-slate-400 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all hover:border-[#2b448f] hover:bg-blue-50/30 hover:text-[#2b448f] group"
                  >
                    <Camera className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                    <span className="text-[11px] font-bold uppercase tracking-widest">Upload Images</span>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleFileChange}
                    />
                  </label>
                  
                  {previews.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50/50 rounded-xl border border-slate-100">
                      {previews.map((src, idx) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group shadow-sm bg-white">
                          <img src={src} alt="Preview" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                          <button 
                            type="button"
                            onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-md transform translate-y-1 group-hover:translate-y-0"
                          >
                            <X className="w-3.5 h-3.5" strokeWidth={3} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
           </div>
        </div>
      </div>
    </Modal>
  );
};
